import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * What the planner shows for an address it does not have.
 *
 * Until this existed, an unmatched /planner/* path rendered the header,
 * the sidebar and nothing else, forever. No error, no redirect, no way to
 * tell a broken link from a page that had not finished loading. A silent
 * void is the worst outcome of a wrong URL, because the person assumes
 * the feature is broken rather than that they are in the wrong place.
 *
 * It does not redirect. Bouncing someone to Discover hides the typo and
 * they make it again; saying what went wrong does not.
 */

/**
 * Pages that live outside the planner shell, so a near-miss can be
 * offered rather than guessed at. /planner/feedbackboard is the one this
 * was written for: the board is a top-level route and the planner prefix
 * is the obvious wrong assumption.
 */
const OUTSIDE = [{ test: /feedbackboard/i, label: 'Feedback board', to: '/feedbackboard' }];

export default function NotFound() {
  const history = useHistory();
  const { pathname } = useLocation();
  const nearby = OUTSIDE.find((page) => page.test.test(pathname));

  return (
    <Box sx={{ padding: '80px 24px', textAlign: 'center' }}>
      <Typography sx={{ fontFamily: "'Lato', sans-serif", fontWeight: 800, fontSize: 20, color: '#0B0D12' }}>
        There is no planner page at this address
      </Typography>
      <Typography
        sx={{ fontFamily: "'Lato', sans-serif", fontSize: 14, color: '#737C8C', marginTop: '8px' }}
      >
        <code>{pathname}</code> did not match anything in the planner.
        {nearby ? ` The ${nearby.label} is not inside the planner — it has its own address.` : ''}
      </Typography>

      <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
        {nearby ? (
          <Button
            variant="contained"
            disableElevation
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}
            onClick={() => history.push(nearby.to)}
          >
            Go to the {nearby.label}
          </Button>
        ) : null}
        <Button
          variant={nearby ? 'outlined' : 'contained'}
          disableElevation
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}
          onClick={() => history.push('/planner/discover')}
        >
          Back to Discover
        </Button>
      </Box>
    </Box>
  );
}
